import { MaterialSymbol } from 'react-material-symbols'
import { useSession } from '../../auth/auth-context'

/**
 * Deep-link from a pattern surface back to the HR catalog row that controls it.
 *
 * HR-only. Renders nothing for non-HR roles so managers and employees never see
 * a "Configure" affordance on patterns they cannot tune.
 *
 * Lands on `/hr/patterns?tab=detection#pattern-<code>` — the catalog page scrolls
 * the targeted row into view and highlights it for 2s.
 */

interface ConfigurePatternLinkProps {
  /** Pattern code, e.g. `frequent_sick_leave`. */
  code: string
  /** Icon-only (default, for tight inline use) or text + icon. */
  variant?: 'icon' | 'text'
}

export function ConfigurePatternLink({
  code,
  variant = 'icon',
}: ConfigurePatternLinkProps): JSX.Element | null {
  const { user } = useSession()
  if (user?.role !== 'hr') return null

  const href = `/hr/patterns?tab=detection#pattern-${code}`

  if (variant === 'text') {
    return (
      <a
        href={href}
        aria-label={`Configure ${code.replace(/_/g, ' ')} threshold`}
        title="Configure this pattern"
        data-testid="configure-pattern-link"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          color: 'var(--text-2)',
          fontSize: 12,
          fontWeight: 500,
          textDecoration: 'none',
          padding: '2px 6px',
          borderRadius: 4,
        }}
      >
        <MaterialSymbol icon="tune" size={14} />
        Configure
      </a>
    )
  }

  return (
    <a
      href={href}
      aria-label={`Configure ${code.replace(/_/g, ' ')} threshold`}
      title="Configure this pattern"
      data-testid="configure-pattern-link"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        color: 'var(--text-3)',
        textDecoration: 'none',
        borderRadius: 4,
      }}
    >
      <MaterialSymbol icon="tune" size={14} />
    </a>
  )
}
